/*
 * Rogerz VAT resolver (Brugtmoms / Alm. moms)
 * -------------------------------------------------------------------------
 * Products created by the VAT solution carry a hidden option named exactly
 * "Type" with two values:
 *   - usedVat  ("Brugtmoms")  -> margin-scheme / used-goods VAT
 *   - fullVat  ("Alm. moms")  -> ordinary VAT
 *
 * The customer never chooses "Type". This script resolves the correct variant
 * for every add-to-cart path (PDP, quick view, collection quick-add):
 *   1. prefer Brugtmoms when available and stocked for the full requested qty
 *      (minus what the customer already has in their cart);
 *   2. otherwise Alm. moms;
 *   3. if the requested qty exceeds available Brugtmoms stock, split the add
 *      into two cart lines (available units Brugtmoms, remainder Alm. moms).
 *
 * Labels come from window.RogerzVat (snippets/rogerz-vat-config.liquid).
 * Per-product variant data comes from <rogerz-vat-data> (snippets/rogerz-vat-data.liquid).
 * Loaded after base.js, which exposes window.__themeCart.
 */
(function () {
  'use strict';

  var ROOT =
    (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var CART_ADD_URL = ROOT + 'cart/add.js';
  var CART_URL = ROOT + 'cart.js';

  var EVENTS = (window.__themeCart && window.__themeCart.EVENTS) || {};
  var CART_CHANGE_EVENT = EVENTS.triggerCartChange || 'action:cart:updateCart';
  var CART_POPUP_EVENT = EVENTS.showCartPopup || 'action:showCartPopup';
  var RESET_BTN_EVENT =
    EVENTS.resetStickyAddTocartButton || 'action:buttonLoadingState:remove';

  function norm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  /* ---- VAT label matching ------------------------------------------------ */
  // Accept every known spelling of the two VAT values (primary label + all
  // vatConfig translations + legacy names) so a localised / renamed option
  // value still resolves.
  function vatAliases() {
    var labels = window.RogerzVat || {};
    var config = labels.vatConfig || {};
    function build(primary, group, legacy) {
      var out = [];
      if (primary) out.push(norm(primary));
      Object.keys(group || {}).forEach(function (key) {
        out.push(norm(group[key]));
      });
      (legacy || []).forEach(function (value) {
        out.push(norm(value));
      });
      return out.filter(function (value, index, all) {
        return value && all.indexOf(value) === index;
      });
    }
    return {
      used: build(labels.usedVat, config.usedVat, ['Brugtmoms']),
      full: build(labels.fullVat, config.fullVat, ['Alm. moms', 'Fuld moms']),
    };
  }

  /* ---- cached cart quantities ------------------------------------------- */
  var cartQuantities = {};
  var cartLoaded = false;
  var cartInFlight = null;

  function loadCartQuantities() {
    // On a collection page every <rogerz-vat-data> connects synchronously on
    // first paint (two form contexts per card). Share one /cart.js request
    // between all of them instead of firing a burst.
    if (cartInFlight) return cartInFlight;
    cartInFlight = fetch(CART_URL, { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        cartQuantities = {};
        (cart.items || []).forEach(function (item) {
          cartQuantities[item.id] =
            (cartQuantities[item.id] || 0) + item.quantity;
        });
        cartLoaded = true;
      })
      .catch(function () {
        // Fail open — Shopify still validates stock on the add request.
      })
      .then(function () {
        cartInFlight = null;
      });
    return cartInFlight;
  }

  document.addEventListener(CART_CHANGE_EVENT, loadCartQuantities);
  document.addEventListener(CART_POPUP_EVENT, loadCartQuantities);

  /* ---- resolver core --------------------------------------------------- */
  function rawStock(variant) {
    if (!variant.inventory_management) return Infinity;
    return Math.max(0, variant.inventory_quantity || 0);
  }

  function effectiveUsedStock(variant) {
    var stock = rawStock(variant);
    if (stock === Infinity) return Infinity;
    return Math.max(0, stock - (cartQuantities[variant.id] || 0));
  }

  // The Brugtmoms / Alm. moms pair whose non-"Type" option values match the
  // currently selected variant.
  function findPair(data, selectedValues) {
    var aliases = vatAliases();
    var typeIndex = data.typeIndex;
    var matches = data.variants.filter(function (variant) {
      return variant.options.every(function (value, index) {
        return (
          index === typeIndex ||
          norm(value) === norm(selectedValues[index])
        );
      });
    });
    return {
      used: matches.find(function (variant) {
        return aliases.used.indexOf(norm(variant.options[typeIndex])) !== -1;
      }),
      full: matches.find(function (variant) {
        return aliases.full.indexOf(norm(variant.options[typeIndex])) !== -1;
      }),
    };
  }

  function preferredVariant(pair, quantity) {
    if (
      pair.used &&
      pair.used.available &&
      effectiveUsedStock(pair.used) >= quantity
    ) {
      return pair.used;
    }
    if (pair.full && pair.full.available) return pair.full;
    if (pair.used && pair.used.available) return pair.used;
    return null;
  }

  function splitPlan(pair, quantity) {
    if (!pair.used || !pair.full || !pair.used.available) return null;
    var stock = effectiveUsedStock(pair.used);
    if (stock === Infinity || stock <= 0 || quantity <= stock) return null;
    return { usedQty: stock, fullQty: quantity - stock };
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  // Single capture-phase listener so a VAT form's submit is resolved / split
  // before ProductForm or ProductCard (bubble-phase, target-registered) run.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      var controller = form.__rogerzVat;
      if (controller && typeof controller.handleSubmit === 'function') {
        controller.handleSubmit(event);
      }
    },
    true
  );

  /* ---- <rogerz-vat-data> ---------------------------------------------- */
  class RogerzVatData extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      try {
        this.data = JSON.parse(
          this.querySelector('script[type="application/json"]').textContent
        );
      } catch (error) {
        return;
      }
      if (
        !this.data ||
        !Array.isArray(this.data.variants) ||
        !(this.data.typeIndex >= 0)
      ) {
        return;
      }

      this.form = this.closest('form');
      if (!this.form) return;
      this.host =
        this.closest('product-form') || this.closest('product-card') || null;
      this.productInfo = this.closest('product-card') ? null : this.closest('[product-info]');
      this.cartType =
        (this.host && this.host.getAttribute('data-cart-type')) ||
        'cart-drawer';
      this.optionScope = this.productInfo || this.form;

      // Register this controller so the single document-level capture-phase
      // submit listener (below) can route the form's submit here BEFORE
      // ProductForm / ProductCard see it.
      this.form.__rogerzVat = this;

      this._syncSoon = debounce(this._sync.bind(this), 60);
      this._onOptionChange = function (event) {
        if (
          event.target &&
          event.target.closest &&
          event.target.closest('[data-vat-type-option]')
        ) {
          return;
        }
        this._syncSoon();
      }.bind(this);

      this._quantityChangeEvent = EVENTS.onQuantityChange || 'on:QuantityChange';
      // Fired by ProductForm whenever the resolved variant id changes
      // (i.e. after every variant-picker round-trip). Re-acquire + re-check.
      this._variantChangeEvent =
        EVENTS.onVariantChangeBasedonElementSelector ||
        'action:update:VariantSelectorChangeBasedOnSelector';

      this.optionScope.addEventListener('change', this._onOptionChange);
      this.optionScope.addEventListener('input', this._onOptionChange);
      // QuantitySelector's +/- dispatch a non-bubbling native 'change'; its
      // debounced 'on:QuantityChange' custom event does bubble — use that.
      this.optionScope.addEventListener(this._quantityChangeEvent, this._syncSoon);
      this.optionScope.addEventListener(this._variantChangeEvent, this._syncSoon);

      var run = function () {
        this._sync();
      }.bind(this);
      if (cartLoaded) {
        run();
      } else {
        loadCartQuantities().then(run);
      }
      // The variant-picker replaces its DOM asynchronously on first paint.
      [150, 500, 1200].forEach(function (delay) {
        setTimeout(run, delay);
      });
    }

    disconnectedCallback() {
      if (this.form && this.form.__rogerzVat === this) {
        delete this.form.__rogerzVat;
      }
      if (this.optionScope) {
        this.optionScope.removeEventListener('change', this._onOptionChange);
        this.optionScope.removeEventListener('input', this._onOptionChange);
        this.optionScope.removeEventListener(
          this._quantityChangeEvent,
          this._syncSoon
        );
        this.optionScope.removeEventListener(
          this._variantChangeEvent,
          this._syncSoon
        );
      }
    }

    /* -- context lookups ------------------------------------------------- */
    _typeSelect() {
      if (!this.productInfo) return null;
      var name = 'options[' + (this.data.typeName || 'Type') + ']';
      // Brackets are literal inside a quoted attribute selector — no escaping.
      return this.productInfo.querySelector(
        'variant-picker select[name="' + name + '"]'
      );
    }

    _typeRadios() {
      if (!this.productInfo) return [];
      var container = this.productInfo.querySelector(
        'variant-picker [data-vat-type-option]'
      );
      return container
        ? Array.prototype.slice.call(
            container.querySelectorAll('input[type="radio"]')
          )
        : [];
    }

    _idInput() {
      return (
        this.form.querySelector('input[name="id"]') ||
        (this.productInfo &&
          this.productInfo.querySelector('product-form input[name="id"]')) ||
        null
      );
    }

    _quantityInput() {
      var formId = this.form.getAttribute('id');
      return (
        (formId &&
          document.querySelector(
            'input[name="quantity"][form="' + formId + '"]'
          )) ||
        this.form.querySelector('[name="quantity"]') ||
        (this.productInfo &&
          this.productInfo.querySelector('[name="quantity"]')) ||
        null
      );
    }

    _quantity() {
      var input = this._quantityInput();
      var value = input ? parseInt(input.value, 10) : 1;
      return value > 0 ? value : 1;
    }

    // Option values of the currently selected variant, aligned to typeIndex order.
    _selectedValues() {
      var input = this._idInput();
      var id = input && parseInt(input.value, 10);
      var variant =
        id &&
        this.data.variants.find(function (item) {
          return item.id === id;
        });
      if (variant) return variant.options.slice();
      try {
        var json = (this.productInfo || document).querySelector(
          'variant-picker script[type="application/json"]'
        );
        if (json) {
          var parsed = JSON.parse(json.textContent);
          if (parsed && parsed.options) return parsed.options.slice();
        }
      } catch (error) {
        /* ignore */
      }
      return this.data.variants[0]
        ? this.data.variants[0].options.slice()
        : [];
    }

    /* -- keep the hidden Type option resolved (PDP / quick view) --------- */
    _sync() {
      if (this._busy) return;
      var select = this._typeSelect();
      var radios = select ? [] : this._typeRadios();
      if (!select && !radios.length) return;

      var pair = findPair(this.data, this._selectedValues());
      var target = preferredVariant(pair, this._quantity());
      if (!target) return;
      var targetValue = target.options[this.data.typeIndex];

      if (select) {
        if (norm(select.value) === norm(targetValue)) return;
        var option = Array.prototype.slice
          .call(select.options)
          .find(function (item) {
            return norm(item.value) === norm(targetValue);
          });
        if (!option) return;
        this._busy = true;
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        var radio = radios.find(function (item) {
          return norm(item.value) === norm(targetValue);
        });
        if (!radio || radio.checked) return;
        this._busy = true;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setTimeout(
        function () {
          this._busy = false;
        }.bind(this),
        300
      );
    }

    /* -- submit: enforce the resolved id, split when needed -------------
       Called from the document-level capture-phase listener, i.e. before
       ProductForm / ProductCard handle the same submit event. */
    handleSubmit(event) {
      if (event.defaultPrevented) return;

      var pair = findPair(this.data, this._selectedValues());
      var quantity = this._quantity();
      var pick = preferredVariant(pair, quantity);

      var idInput = this._idInput();
      if (pick && idInput && parseInt(idInput.value, 10) !== pick.id) {
        idInput.value = String(pick.id);
      }

      var plan = splitPlan(pair, quantity);
      if (!plan) return; // normal single-line add proceeds with the resolved id

      // Stop the theme's own handlers; add the two VAT lines ourselves.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this._splitAdd(pair, plan);
    }

    _collectProperties() {
      var properties = {};
      Array.prototype.slice
        .call(this.form.querySelectorAll('[name^="properties["]'))
        .forEach(function (input) {
          var match = input.name.match(/^properties\[(.+)\]$/);
          if (!match) return;
          if (input.type === 'checkbox') {
            if (input.checked) properties[match[1]] = input.value || 'on';
            return;
          }
          if (input.value !== '') properties[match[1]] = input.value;
        });
      return properties;
    }

    _splitAdd(pair, plan) {
      var util = window.__themeCart && window.__themeCart.UTIL;
      var button =
        this.form.querySelector('[name="add"]') ||
        this.form.querySelector('[type="submit"]');
      var buttonState = util && button ? util.submitButtonState(button) : null;
      if (buttonState) buttonState.loadingState();

      var sections =
        util && util.sectionRenderUtil
          ? util.sectionRenderUtil(this.cartType).getSectionsString()
          : '';
      var properties = this._collectProperties();
      var payload = {
        items: [
          { id: pair.used.id, quantity: plan.usedQty, properties: properties },
          { id: pair.full.id, quantity: plan.fullQty, properties: properties },
        ],
        sections: sections,
        sections_url: window.location.pathname,
      };

      var self = this;
      fetch(CART_ADD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
        credentials: 'same-origin',
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (error) {
              throw error;
            });
          }
          return response.json();
        })
        .then(function (data) {
          if (buttonState) buttonState.completeState();
          document.dispatchEvent(
            new CustomEvent(CART_CHANGE_EVENT, {
              detail: { sections: data.sections },
            })
          );
          document.dispatchEvent(new CustomEvent(RESET_BTN_EVENT));
          setTimeout(function () {
            if (buttonState) {
              buttonState.resetState(1000);
              buttonState.disableState(false);
            }
          }, 1000);
        })
        .catch(function (error) {
          console.error('[rogerz-vat] split add failed', error);
          // Last resort: let the browser submit the form normally.
          self.form.submit();
        });
    }
  }

  if (!customElements.get('rogerz-vat-data')) {
    customElements.define('rogerz-vat-data', RogerzVatData);
  }

  /* ---- <rogerz-vat-disclaimer> ---------------------------------------- */
  // Shows the brugtmoms disclaimer while a Brugtmoms variant is selected.
  // The variant picker dispatches a bubbling `change` on the product form's
  // input[name="id"] after every variant switch (base.js updateVariantInput).
  // Only that section's form counts (data-form-id), so other product forms in
  // the section (e.g. complementary products) can't toggle the notice.
  // The element stays in the DOM as an aria-live region; only the inner
  // .rogerz-vat-disclaimer (role="note") is shown / hidden.
  class RogerzVatDisclaimer extends HTMLElement {
    connectedCallback() {
      this._ids = (this.dataset.variantIds || '').split(',').filter(Boolean);
      this._formId = this.dataset.formId;
      this._notice = this.querySelector('.rogerz-vat-disclaimer');
      this._scope = this.closest('[product-info]') || document;
      this._onChange = (event) => {
        const input = event.target;
        if (!input || input.name !== 'id' || input.tagName !== 'INPUT') return;
        if (!this._notice) return;
        if (this._formId && (!input.form || input.form.id !== this._formId)) return;
        this._notice.hidden = !this._ids.includes(String(input.value));
      };
      this._scope.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      if (this._scope) this._scope.removeEventListener('change', this._onChange);
    }
  }

  if (!customElements.get('rogerz-vat-disclaimer')) {
    customElements.define('rogerz-vat-disclaimer', RogerzVatDisclaimer);
  }
})();
